import { Button } from "@mixitone/components";
import { Account } from "@mixitone/models";
import ApplicationController, { ApplicationView } from "@mixitone/mvc";
import { mixitoneImagesTransformedUrl } from "@mixitone/util";
import { captureException } from "@mixitone/telemetry";
import { uploadMixitoneImageObject } from "lib/mixitoneImagesUpload";

interface ImageUploadProps {
  /**
   * Current image ID value
   */
  value?: string;

  /**
   * Callback when image changes
   */
  onChange: (value: string) => void;

  /**
   * Whether the upload is disabled
   */
  disabled?: boolean;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Alt text for the image
   */
  alt?: string;
}

interface State {
  loading: boolean;
  value?: string;
  url?: string;
  error?: string;
  uploading: boolean;
}

class ImageUploadController extends ApplicationController<State, ImageUploadProps> {
  get initialState(): State {
    return {
      loading: false,
      uploading: false,
    };
  }

  onChange: (value: string) => void = () => {};

  async initialize(props: ImageUploadProps) {
    await this.changeProps(props);
  }

  async changeProps(props: ImageUploadProps) {
    this.onChange = props.onChange;

    if (props.value !== this.state.value) {
      this.state.value = props.value;
      await this.loadImageUrl();
    }
  }

  async loadImageUrl() {
    if (!this.state.value) {
      this.state.url = undefined;
      return;
    }

    try {
      const accountId = Account.current?.id;
      if (!accountId) {
        this.state.url = undefined;
        return;
      }
      this.state.url = mixitoneImagesTransformedUrl(`${accountId}/${this.state.value}`, {
        width: 800,
        fit: "scale-down",
        format: "auto",
      });
    } catch (error) {
      captureException(error);
      console.error("Error loading image URL:", error);
      this.state.error = "Error loading image";
    }
  }

  async actionUpload(file: File) {
    if (!file) return;

    this.state.uploading = true;

    try {
      const accountId = Account.current?.id;
      if (!accountId) return;

      const uid = crypto.randomUUID();
      await uploadMixitoneImageObject({
        accountId,
        relativePath: uid,
        file,
      });

      this.onChange(uid);
    } catch (error) {
      console.error("Error uploading image:", error);
      this.state.error = "Error uploading image";
    } finally {
      this.state.uploading = false;
    }
  }
}

/**
 * Component for uploading and displaying images
 */
const UncontrolledImageUpload = ({
  disabled = false,
  className = "",
  alt = "Uploaded image",
}: ImageUploadProps) => {
  const controller = ImageUploadController.use();
  const { url, uploading } = controller.state;

  const upload = async (file: File) => {
    controller.actionUpload(file);
  };

  const handleFileSelect: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) upload(file);
    };
    input.click();
  };

  if (url) {
    return (
      <div className={`relative w-full ${className}`}>
        <img src={url} alt={alt} className="h-48 w-full rounded-lg object-cover" />
        <Button
          kind="secondary"
          className="absolute bottom-2 right-2"
          onClick={handleFileSelect}
          disabled={disabled || uploading}
        >
          Change banner
        </Button>
      </div>
    );
  }

  return (
    <div className={`flex h-48 w-full items-center justify-center rounded-lg bg-gray-100 ${className}`}>
      <Button kind="secondary" onClick={handleFileSelect} disabled={disabled || uploading}>
        {uploading ? "Uploading..." : "Add banner image"}
      </Button>
    </div>
  );
};

const ImageUpload = ImageUploadController.scope(ApplicationView(UncontrolledImageUpload));
export { ImageUpload };
